const STEPS = [
  {
    title: 'modesTitle',
    fallbackTitle: 'Browse your way',
    body: 'modesBody',
    fallbackBody:
      'Wall: mixed posts. Visual: photos and videos. Explore: image grid. Talk: text posts. Circles: group chats.',
    icons: ['home', 'image', 'grid-2x2', 'message-circle', 'users'],
  },
  {
    title: 'sharingTitle',
    fallbackTitle: 'Choose who sees your posts',
    body: 'sharingBody',
    fallbackBody:
      'Choose an audience before you post. People who can see your post can still copy it.',
    icons: ['globe', 'lock'],
  },
  {
    title: 'controlTitle',
    fallbackTitle: 'Choose what you see',
    body: 'controlBody',
    fallbackBody:
      'Follow people and pages, or unfollow anytime. Search finds posts and people. Change feed preferences in Settings, or choose Chronological for posts in time order.',
    icons: ['search', 'settings'],
  },
];

/** Optional, replayable help and desired-state starter follows. Rendering and skipping
 * tutorial cards never mutate relationships; accepted server snapshots own all changes.
 */
export function createStarterTour(ctx) {
  let owner,
    step = 0,
    skipped = false,
    selected = new Set(),
    busy = false,
    intent,
    recommended = false,
    followingStamp,
    packQuery = '',
    packCategory = 'all',
    generation = 0;
  const t = (key, fallback) => {
    const text = ctx.translate(`tour.${key}`);
    return text === `tour.${key}` ? fallback : text;
  };
  function initialize() {
    const currentFollows = JSON.stringify([...(ctx.state.following ?? [])].sort());
    if (owner === ctx.state.me.id && followingStamp === currentFollows) return;
    owner = ctx.state.me.id;
    followingStamp = currentFollows;
    step = 0;
    skipped = false;
    packQuery = '';
    packCategory = 'all';
    const catalog = ctx.state.starterPacks ?? [];
    recommended = ctx.state.account?.starterStatus === 'pending' && !ctx.state.following?.length;
    selected = new Set(
      catalog
        .filter((pack) =>
          recommended
            ? ['chicago', 'space', 'food'].includes(pack.id)
            : ctx.state.following?.includes(pack.pageId),
        )
        .map((pack) => pack.id),
    );
    busy = false;
    intent = undefined;
  }
  async function finish(action, error, controls) {
    if (busy) return;
    if (ctx.state.account?.emailVerified !== true) return ctx.go('verify-email');
    busy = true;
    const disabled = controls.map((control) => control.disabled === true);
    controls.forEach((control) => {
      control.disabled = true;
    });
    error.textContent = '';
    const actor = owner;
    const current = generation;
    const body = {
      action,
      packs: action === 'save' ? [...selected].sort() : [],
      tourStatus: skipped ? 'dismissed' : 'complete',
    };
    const signature = JSON.stringify(body);
    if (intent?.signature !== signature) intent = { signature, requestId: crypto.randomUUID() };
    try {
      const snapshot = await ctx.api('/api/account/starter', {
        ...body,
        requestId: intent.requestId,
      });
      if (generation !== current || ctx.state?.me?.id !== actor || ctx.route()[0] !== 'starter')
        return;
      ctx.state = snapshot;
      owner = undefined;
      ctx.go(ctx.referralDestination?.() ?? 'feed');
    } catch (failure) {
      if (generation === current && ctx.state?.me?.id === actor && ctx.route()[0] === 'starter')
        error.textContent = ctx.uiText(failure.message);
    } finally {
      if (generation === current && owner === actor) busy = false;
      controls.forEach((control, index) => {
        control.disabled = disabled[index];
      });
    }
  }
  function disposeStarterTour() {
    generation++;
    busy = false;
  }
  function renderStarterTour() {
    disposeStarterTour();
    if (ctx.state.account?.emailVerified !== true) return ctx.go('verify-email');
    initialize();
    const panel = ctx.e('section', 'setup-card form-stack starter-tour');
    const error = ctx.e('p', 'form-error');
    error.setAttribute('role', 'alert');
    const controls = [];
    let title;
    if (step < STEPS.length) {
      const guide = STEPS[step];
      panel.append(
        ctx.e(
          'p',
          'eyebrow',
          `${t('optionalTour', 'TOUR · OPTIONAL')} · ${step + 1}/${STEPS.length}`,
        ),
      );
      title = ctx.e('h1', '', t(guide.title, guide.fallbackTitle));
      const content = ctx.e('div', 'tour-guide');
      const icons = ctx.e('div', 'tour-icons');
      for (const name of guide.icons) icons.append(ctx.icon(name));
      icons.setAttribute('aria-hidden', 'true');
      content.append(icons, ctx.e('p', '', t(guide.body, guide.fallbackBody)));
      const back = ctx.button(
        t('back', 'Back'),
        () => {
          if (!busy && step > 0) {
            step--;
            renderStarterTour();
          }
        },
        'secondary-button',
      );
      back.disabled = step === 0;
      const skip = ctx.button(
        t('skipTour', 'Skip tour'),
        () => {
          if (!busy) {
            skipped = true;
            step = STEPS.length;
            renderStarterTour();
          }
        },
        'text-button',
      );
      const next = ctx.button(
        t('next', 'Next'),
        () => {
          if (!busy) {
            step++;
            renderStarterTour();
          }
        },
        'primary-button',
      );
      panel.append(title, content, ctx.append(ctx.e('div', 'setup-actions'), back, skip, next));
    } else {
      title = ctx.e('h1', '', t('packsTitle', 'Choose pages to follow'));
      panel.append(
        title,
        ctx.e(
          'p',
          '',
          t('packsIntro', 'Pick subjects you want in your feed. You can unfollow anytime.'),
        ),
      );
      const selectedCopy = () =>
        t('recommended', '{count} pages selected. Change the selection or skip.').replace(
          '{count}',
          String(selected.size),
        );
      const selectionNote = ctx.e('p', 'subtle', selectedCopy());
      selectionNote.setAttribute('aria-live', 'polite');
      panel.append(selectionNote);
      const catalog = ctx.state.starterPacks ?? [];
      const search = ctx.field('input', {
        type: 'search',
        value: packQuery,
        maxLength: 120,
        placeholder: t('packSearch', 'Find an interest or subject'),
        ariaLabel: t('packSearch', 'Find an interest or subject'),
      });
      const categories = ctx.e('div', 'starter-pack-categories');
      const categoryChoices = [];
      const cards = [];
      const visibleNote = ctx.e('p', 'subtle');
      visibleNote.setAttribute('aria-live', 'polite');
      const filterCards = () => {
        let count = 0;
        for (const { pack, card } of cards) {
          card.hidden =
            (packCategory !== 'all' && (pack.category || 'Interests') !== packCategory) ||
            !`${pack.id ?? ''} ${(pack.id ?? '').replaceAll('-', ' ')} ${pack.title} ${pack.description} ${pack.category ?? ''} ${(pack.tags ?? []).join(' ')}`
              .toLocaleLowerCase()
              .includes(packQuery.toLocaleLowerCase());
          if (!card.hidden) count++;
        }
        visibleNote.textContent = count
          ? `${count} / ${catalog.length} ${t('packsAvailable', 'pages')}`
          : t('noPacks', 'No matching pages. Try another subject.');
        for (const { value, button } of categoryChoices)
          button.setAttribute('aria-pressed', String(value === packCategory));
      };
      search.addEventListener('input', () => {
        packQuery = search.value.trim();
        filterCards();
      });
      controls.push(search);
      for (const value of [
        'all',
        ...new Set(catalog.map((pack) => pack.category || 'Interests')),
      ]) {
        const choice = ctx.button(
          value === 'all' ? t('allPacks', 'All') : value,
          () => {
            if (busy) return;
            packCategory = value;
            filterCards();
          },
          'explore-chip',
        );
        categoryChoices.push({ value, button: choice });
        controls.push(choice);
        categories.append(choice);
      }
      if (catalog.length) panel.append(search, categories, visibleNote);
      const grid = ctx.e('div', 'starter-pack-grid');
      for (const pack of catalog) {
        const card = ctx.e('label', 'starter-pack');
        const checkbox = ctx.field('input', { type: 'checkbox', checked: selected.has(pack.id) });
        checkbox.addEventListener('change', () => {
          if (busy) return;
          if (checkbox.checked) selected.add(pack.id);
          else selected.delete(pack.id);
          intent = undefined;
          selectionNote.textContent = selectedCopy();
        });
        controls.push(checkbox);
        if (pack.coverImage)
          card.append(ctx.field('img', { src: pack.coverImage, alt: '', loading: 'lazy' }));
        const copy = ctx.append(
          ctx.e('span', 'starter-pack-copy'),
          ctx.e('strong', '', pack.title),
          ctx.e('span', 'subtle', pack.description),
        );
        card.append(copy, checkbox);
        grid.append(card);
        cards.push({ pack, card });
      }
      filterCards();
      if (catalog.length)
        panel.append(
          grid,
          ctx.e(
            'p',
            'subtle',
            t(
              'followDisclosure',
              'When you save, you follow checked pages and unfollow unchecked pages in this list. Skip makes no changes.',
            ),
          ),
        );
      else
        panel.append(
          ctx.e(
            'p',
            'subtle',
            t('unavailable', 'Pages could not load. Skip this step and try again later.'),
          ),
        );
      panel.append(ctx.e('p', 'subtle', t('independent', 'Pages written by myAudience.')));
      const credits = ctx.e('a', 'text-button', t('credits', 'Sources and image credits'));
      credits.href = '/credits.html';
      panel.append(credits);
      const back = ctx.button(
        t('back', 'Back'),
        () => {
          if (!busy) {
            step--;
            renderStarterTour();
          }
        },
        'secondary-button',
      );
      const skip = ctx.button(
        t('skipPacks', 'Skip this step'),
        () => finish('dismiss', error, controls),
        'text-button',
      );
      const save = ctx.button(
        t('save', 'Save and open feed'),
        () => finish('save', error, controls),
        'primary-button',
      );
      save.disabled = catalog.length === 0;
      controls.push(back, skip, save);
      panel.append(error, ctx.append(ctx.e('div', 'setup-actions'), back, skip, save));
    }
    title.tabIndex = -1;
    ctx.view.replaceChildren(panel);
    title.focus({ preventScroll: true });
  }
  return { renderStarterTour, disposeStarterTour };
}
